import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useId,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from "react";

import type { TipsContent } from "./Tips";

export interface TipsRailEntry {
	id: string;
	content: TipsContent;
}

interface TipsRailValue {
	/** False where the right-hand space is already spoken for, or outside the builder. */
	enabled: boolean;
	entries: TipsRailEntry[];
	register: (id: string, content: TipsContent) => void;
	unregister: (id: string) => void;
}

const NO_RAIL: TipsRailValue = {
	enabled: false,
	entries: [],
	register: () => {},
	unregister: () => {},
};

const TipsRailContext = createContext<TipsRailValue>(NO_RAIL);

export const useTipsRail = () => useContext(TipsRailContext);

export function TipsRailProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
	const [contentById, setContentById] = useState<Record<string, TipsContent>>({});

	const register = useCallback((id: string, content: TipsContent) => {
		setContentById((current) => (current[id] === content ? current : { ...current, [id]: content }));
	}, []);

	const unregister = useCallback((id: string) => {
		setContentById((current) => {
			if (!(id in current)) return current;
			const { [id]: _removed, ...rest } = current;
			return rest;
		});
	}, []);

	const value = useMemo<TipsRailValue>(
		() => ({
			enabled,
			entries: Object.entries(contentById).map(([id, content]) => ({ id, content })),
			register,
			unregister,
		}),
		[enabled, contentById, register, unregister],
	);

	return <TipsRailContext.Provider value={value}>{children}</TipsRailContext.Provider>;
}

/**
 * Publishes a section's tips to the rail, and reports whether the rail took them.
 * A `false` answer leaves the caller showing its own popover trigger — that is the
 * contact screen, whose right-hand space already holds the preview, and every
 * screen outside the builder.
 *
 * Registration happens in a layout effect, not an ordinary one: the layout sizes
 * its columns from whether anything registered, so an effect that ran after paint
 * would show one frame of full-width content before the rail pushed it back in.
 *
 * The effect keys on the content's value rather than its identity, so a caller
 * that builds its tips inline re-registers only on a real change instead of
 * looping every render.
 */
export function useRailTips(content: TipsContent): boolean {
	const { enabled, register, unregister } = useTipsRail();
	const id = useId();

	const contentRef = useRef(content);
	contentRef.current = content;
	const contentKey = JSON.stringify(content);

	useLayoutEffect(() => {
		if (!enabled) return;

		register(id, contentRef.current);
		return () => unregister(id);
	}, [enabled, id, contentKey, register, unregister]);

	return enabled;
}
